import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, X, ShieldCheck, FileText, PlayCircle, Sparkles, Users, Target, ChevronDown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { metaTrack, metaConsentGranted } from "@/lib/metaPixel";
import { toast } from "sonner";

const HERO = "https://static.prod-images.emergentagent.com/jobs/72130633-4d95-425a-bae8-f0a72af37042/images/66c5779ed718d9757f43b3b507f755da0d954ecb29821eda0596468f9e2683ed.jpeg";

const MODULES = [
  "Ripartire con metodo e obiettivi chiari",
  "Scegliere e impostare gli strumenti giusti",
  "Costruire un profilo credibile e autentico",
  "Gestire i primi contatti con serenità",
  "Organizzare le conversazioni senza ansia",
  "Riconoscere i segnali e proteggere il tuo tempo",
  "Passare al primo incontro con equilibrio",
];
const FAQS = [
  ["A chi è adatto questo percorso?", "A chi ha superato i 45 anni e vuole tornare a conoscere nuove persone con un metodo pratico, chiaro e senza fretta."],
  ["È un corso teorico?", "No. È un percorso operativo: contenuti strutturati, esercizi e strumenti pronti da usare passo dopo passo."],
  ["Che differenza c'è tra Digital e Premium?", "Digital include l'introduzione e i 7 moduli in PDF. Premium aggiunge il Toolkit Bonus e le 8 video lezioni."],
  ["Come accedo ai contenuti dopo l'acquisto?", "Dopo il pagamento i contenuti si sbloccano nella tua area riservata: i PDF sono scaricabili e i video si guardano online."],
];

const Section = ({ children, className = "" }) => (
  <section className={`max-w-5xl mx-auto px-4 py-12 sm:py-16 ${className}`}>{children}</section>
);

const RestartDating = () => {
  const { lang } = useI18n();
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const adminPreview = params.get("admin_preview");
  const isAdmin = user?.role === "admin";
  const [versions, setVersions] = useState([]);
  const [owned, setOwned] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const digital = versions.find((v) => v.tier === "digital");
  const premium = versions.find((v) => v.tier === "premium");

  // Meta Pixel ViewContent: una sola volta all'apertura della pagina (dopo consenso)
  const vcFired = useRef(false);
  useEffect(() => {
    const fire = () => {
      if (vcFired.current) return;
      vcFired.current = true;
      metaTrack("ViewContent", { content_name: "Restart Dating 45+", content_type: "product" });
    };
    if (metaConsentGranted()) { fire(); return; }
    window.addEventListener("lsh-consent-granted", fire, { once: true });
    return () => window.removeEventListener("lsh-consent-granted", fire);
  }, []);

  const loadOwned = useCallback(async (vers) => {
    if (!user) return;
    if (user.role === "admin" && (adminPreview === "digital" || adminPreview === "premium")) {
      const v = vers.find((x) => x.tier === adminPreview);
      if (v) {
        try { const { data } = await api.get(`/my/guided/${v.id}`); setOwned({ ...data, adminPreview: true }); } catch {}
      }
      return;
    }
    for (const v of vers) {
      try {
        const { data } = await api.get(`/my/guided/${v.id}`);
        if (v.tier === "premium") { setOwned(data); return; }
        setOwned(data);
      } catch {}
    }
  }, [user, adminPreview]);

  useEffect(() => {
    api.get("/guided-paths/restart-dating-45").then(({ data }) => {
      setVersions(data.versions); loadOwned(data.versions);
    }).catch(() => {});
  }, [loadOwned]);

  const choose = (v) => {
    if (!v) return;
    addItem({ id: v.id, sku: v.sku, title_it: v.title_it, title_en: v.title_en, price: v.price, image_url: HERO });
    toast.success(lang === "en" ? "Version added to cart" : "Versione aggiunta al carrello");
    navigate("/carrello");
  };

  const downloadPdf = async (pid, idx, label) => {
    try {
      const res = await api.get(`/guided/${pid}/content/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${label}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Contenuto non disponibile"); }
  };

  const money = (v) => `€${Number(v).toFixed(2).replace(".", ",")}`;

  return (
    <div data-testid="restart-dating-page" className="bg-white">
      {/* HERO */}
      <div className="relative overflow-hidden bg-brand-navy text-white">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-5xl mx-auto px-4 py-20 sm:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange/20 text-brand-orange px-3 py-1 text-xs font-bold uppercase tracking-wider">Percorsi Guidati</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold leading-tight">Restart Dating 45+</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-white/80">Percorso pratico per tornare a conoscere persone con più chiarezza, metodo e serenità.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="hero-choose-digital" onClick={() => choose(digital)} className="rounded-full h-12 px-6 bg-white text-brand-navy hover:bg-white/90 font-semibold">Scegli Digital · {digital ? money(digital.price) : "…"}</Button>
            <Button data-testid="hero-choose-premium" onClick={() => choose(premium)} className="rounded-full h-12 px-6 bg-brand-electric hover:bg-brand-electric/90 text-white font-semibold">Scegli Premium · {premium ? money(premium.price) : "…"}</Button>
          </div>
        </div>
      </div>

      {/* Barra Admin: anteprima senza acquisto */}
      {isAdmin && (
        <div data-testid="admin-preview-bar" className="bg-brand-orange/10 border-b border-brand-orange/30">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange">Strumenti Admin</span>
            <Link to="/percorsi/restart-dating-45?admin_preview=digital" data-testid="admin-preview-digital" className={`text-sm rounded-full px-4 py-1.5 border ${adminPreview === "digital" ? "bg-brand-navy text-white border-brand-navy" : "border-brand-navy/20 text-brand-navy"}`}>Anteprima Digital</Link>
            <Link to="/percorsi/restart-dating-45?admin_preview=premium" data-testid="admin-preview-premium" className={`text-sm rounded-full px-4 py-1.5 border ${adminPreview === "premium" ? "bg-brand-electric text-white border-brand-electric" : "border-brand-navy/20 text-brand-navy"}`}>Anteprima Premium</Link>
            {adminPreview && <Link to="/percorsi/restart-dating-45" className="text-xs text-brand-navy/50 underline">Esci dall'anteprima</Link>}
          </div>
        </div>
      )}

      {/* AREA RISERVATA (se acquistato o anteprima admin) */}
      {owned && (
        <Section className="!py-10">
          <div data-testid="owned-content-area" className="rounded-2xl border border-brand-electric/30 bg-brand-light/60 p-6">
            {owned.adminPreview && (
              <div data-testid="admin-preview-badge" className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-orange text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5">
                Anteprima Admin — {owned.tier === "premium" ? "Premium" : "Digital"} (nessun acquisto)
              </div>
            )}
            <h2 className="text-xl font-heading font-bold text-brand-navy flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand-electric" /> I tuoi contenuti ({owned.tier === "premium" ? "Premium" : "Digital"})</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {owned.items.map((it) => (
                <div key={it.index} className="rounded-xl border border-brand-navy/10 bg-white p-3">
                  <p className="text-sm font-medium text-brand-navy flex items-center gap-2">
                    {it.type === "video" ? <PlayCircle className="h-4 w-4 text-brand-electric" /> : <FileText className="h-4 w-4 text-brand-orange" />} {it.label}
                  </p>
                  {!it.ready ? (
                    <p className="mt-1 text-xs text-brand-navy/40">In arrivo</p>
                  ) : it.type === "video" ? (
                    <video data-testid={`video-${it.index}`} controls preload="metadata" className="mt-2 w-full rounded-lg bg-black"
                      src={`${API}/guided/${owned.product_id}/content/${it.index}`} />
                  ) : (
                    <Button data-testid={`pdf-${it.index}`} size="sm" variant="outline" onClick={() => downloadPdf(owned.product_id, it.index, it.label)} className="mt-2 rounded-full text-xs gap-1"><FileText className="h-3 w-3" /> Scarica PDF</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* DESCRIZIONE */}
      <Section>
        <h2 className="text-2xl font-heading font-bold text-brand-navy">Il percorso</h2>
        <p className="mt-3 text-brand-navy/70 leading-relaxed">Restart Dating 45+ è un percorso digitale guidato che ti accompagna, passo dopo passo, nel tornare a conoscere nuove persone. Niente promesse: solo un metodo concreto, strumenti pratici e contenuti strutturati per muoverti con chiarezza e tranquillità.</p>
        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          <div className="rounded-xl border border-brand-navy/10 p-5">
            <h3 className="font-semibold text-brand-navy flex items-center gap-2"><Users className="h-4 w-4 text-brand-electric" /> A chi è rivolto</h3>
            <p className="mt-2 text-sm text-brand-navy/70">A chi, dopo i 45 anni, vuole rimettersi in gioco con un approccio maturo, pratico e senza pressioni.</p>
          </div>
          <div className="rounded-xl border border-brand-navy/10 p-5">
            <h3 className="font-semibold text-brand-navy flex items-center gap-2"><Target className="h-4 w-4 text-brand-electric" /> Cosa imparerai</h3>
            <p className="mt-2 text-sm text-brand-navy/70">A impostare obiettivi chiari, usare gli strumenti giusti, gestire contatti e conversazioni e arrivare al primo incontro con equilibrio.</p>
          </div>
        </div>
      </Section>

      {/* MODULI */}
      <Section className="!pt-0">
        <h2 className="text-2xl font-heading font-bold text-brand-navy">I 7 moduli</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          {MODULES.map((m, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-brand-navy/10 p-4">
              <span className="shrink-0 h-7 w-7 rounded-full bg-brand-electric text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
              <p className="text-sm text-brand-navy/80">{m}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SCELTA VERSIONI */}
      <Section className="!pt-0">
        <h2 className="text-2xl font-heading font-bold text-brand-navy text-center">Scegli la tua versione</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div data-testid="version-digital" className="rounded-2xl border border-brand-navy/15 p-6 flex flex-col">
            <h3 className="text-lg font-heading font-bold text-brand-navy">Digital</h3>
            <p className="mt-1 text-3xl font-extrabold text-brand-navy">{digital ? money(digital.price) : "…"}</p>
            <ul className="mt-4 space-y-2 text-sm text-brand-navy/70 flex-1">
              <li className="flex gap-2"><Check className="h-4 w-4 text-brand-electric" /> Modulo introduttivo (PDF)</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-brand-electric" /> 7 moduli in PDF</li>
            </ul>
            <Button data-testid="choose-digital" onClick={() => choose(digital)} variant="outline" className="mt-6 rounded-full h-11 border-brand-navy/20 text-brand-navy">Scegli Digital</Button>
          </div>
          <div data-testid="version-premium" className="relative rounded-2xl border-2 border-brand-electric p-6 flex flex-col">
            <span className="absolute -top-3 left-6 bg-brand-electric text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Più completa</span>
            <h3 className="text-lg font-heading font-bold text-brand-navy">Premium</h3>
            <p className="mt-1 text-3xl font-extrabold text-brand-navy">{premium ? money(premium.price) : "…"}</p>
            <ul className="mt-4 space-y-2 text-sm text-brand-navy/70 flex-1">
              <li className="flex gap-2"><Check className="h-4 w-4 text-brand-electric" /> Tutto ciò che c'è in Digital</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-brand-electric" /> Restart Dating Toolkit (PDF)</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-brand-electric" /> 8 video lezioni online</li>
            </ul>
            <Button data-testid="choose-premium" onClick={() => choose(premium)} className="mt-6 rounded-full h-11 bg-brand-electric hover:bg-brand-electric/90 text-white">Scegli Premium</Button>
          </div>
        </div>
      </Section>

      {/* CONFRONTO */}
      <Section className="!pt-0">
        <h2 className="text-2xl font-heading font-bold text-brand-navy">Confronto versioni</h2>
        <div className="mt-6 overflow-x-auto rounded-xl border border-brand-navy/10">
          <table className="w-full text-sm">
            <thead className="bg-brand-light text-brand-navy/70">
              <tr><th className="text-left px-4 py-3 font-medium">Contenuto</th><th className="px-4 py-3 font-medium">Digital</th><th className="px-4 py-3 font-medium text-brand-electric">Premium</th></tr>
            </thead>
            <tbody className="text-brand-navy/80">
              {[["Introduzione PDF", true, true], ["7 Moduli PDF", true, true], ["Toolkit Bonus", false, true], ["8 Video Lezioni", false, true]].map(([l, d, p], i) => (
                <tr key={i} className="border-t border-brand-navy/5">
                  <td className="px-4 py-3">{l}</td>
                  <td className="px-4 py-3 text-center">{d ? <Check className="h-4 w-4 text-brand-electric inline" /> : <X className="h-4 w-4 text-brand-navy/25 inline" />}</td>
                  <td className="px-4 py-3 text-center">{p ? <Check className="h-4 w-4 text-brand-electric inline" /> : <X className="h-4 w-4 text-brand-navy/25 inline" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* TOOLKIT + VIDEO */}
      <Section className="!pt-0 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-brand-navy text-white p-6">
          <h3 className="font-heading font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand-orange" /> Restart Dating Toolkit <span className="text-xs bg-brand-orange/20 text-brand-orange px-2 py-0.5 rounded-full">Premium</span></h3>
          <ul className="mt-3 space-y-1.5 text-sm text-white/80">
            {["15 bio pronte da personalizzare", "30 aperture di conversazione", "Checklist delle 5 fotografie", "10 modi per proporre un incontro", "Checklist Red Flag Restart", "Restart 7 in una pagina"].map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-brand-orange shrink-0" /> {x}</li>)}
          </ul>
        </div>
        <div className="rounded-2xl border border-brand-navy/10 p-6">
          <h3 className="font-heading font-bold text-brand-navy flex items-center gap-2"><PlayCircle className="h-5 w-5 text-brand-electric" /> 8 video lezioni <span className="text-xs bg-brand-electric/10 text-brand-electric px-2 py-0.5 rounded-full">Premium</span></h3>
          <p className="mt-3 text-sm text-brand-navy/70">Le video lezioni (introduzione + 7 moduli) si guardano online nella tua area riservata, senza scaricare file pesanti.</p>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="!pt-0">
        <h2 className="text-2xl font-heading font-bold text-brand-navy">Domande frequenti</h2>
        <div className="mt-6 space-y-3">
          {FAQS.map(([q, a], i) => (
            <div key={i} className="rounded-xl border border-brand-navy/10">
              <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-brand-navy">
                {q} <ChevronDown className={`h-4 w-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="px-4 pb-4 text-sm text-brand-navy/70">{a}</p>}
            </div>
          ))}
        </div>
      </Section>

      {/* CTA FINALE */}
      <div className="bg-brand-light">
        <Section className="text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-brand-navy">Pronto a ripartire?</h2>
          <p className="mt-2 text-brand-navy/70">Scegli la versione più adatta a te e inizia il percorso.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button data-testid="cta-choose-digital" onClick={() => choose(digital)} variant="outline" className="rounded-full h-12 px-6 border-brand-navy/20 text-brand-navy">Scegli Digital · {digital ? money(digital.price) : "…"}</Button>
            <Button data-testid="cta-choose-premium" onClick={() => choose(premium)} className="rounded-full h-12 px-6 bg-brand-electric hover:bg-brand-electric/90 text-white">Scegli Premium · {premium ? money(premium.price) : "…"}</Button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default RestartDating;
